import React, { Suspense } from "react";
import { createStore, applyMiddleware, compose } from "redux";
import { Switch, BrowserRouter, Redirect } from "react-router-dom";
import { routerMiddleware, ConnectedRouter } from "connected-react-router";
import { Provider } from "react-redux";
import thunk from "redux-thunk";
import { Helmet } from "react-helmet";
import { ToastContainer } from "react-toastify";
import history from "utils/history";
import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { CookiesProvider } from "react-cookie";

import "i18n";

import {
  TransportTaskDetailPage,
  TransportTaskListPage,
} from "containers/TransportTasks";
import Drivers from "containers/Drivers";
import DriverConversations from "containers/DriverConversations";
import BusinessRules from "containers/BusinessRules";
import ReportQueue from "containers/ReportQueue";
//Import Components
import PrivateRoute from "components/PrivateRoute";
import Navbar from "components/Navbar";
import ModalMain from "components/ModalMain";
import LoadingOverlay from "components/LoadingOverlay";

//Import Reducer
import rootReducer from "reducers/rootReducer";

import { sec } from "utils/security";
import { acquireApiToken, selectAccount } from "auth/entra";
import Reactotron from '../ReactotronConfig'


//Import Styles
import "./App.scss";

//Create redux store
const appStore = createStore(
  rootReducer(history),
  compose(applyMiddleware(routerMiddleware(history), thunk), Reactotron.createEnhancer())
);

export function AppRoutes() {
  return (
    <Switch>
      <PrivateRoute
        exact
        path="/transport-tasks/:id"
        component={TransportTaskDetailPage}
        key="transport-task-detail"
      />
      <PrivateRoute
        exact
        path="/transport-tasks"
        component={TransportTaskListPage}
        key="transport-task-list"
      />
      <PrivateRoute exact path="/drivers" component={Drivers} key="drivers" />
      <PrivateRoute exact path="/driver-conversations" component={DriverConversations} key="driver-conversations" />
      <PrivateRoute exact path="/business-rules" component={BusinessRules} key="business-rules" />
      <PrivateRoute exact path="/reports" component={ReportQueue} key="reports" />
      <Redirect to="/transport-tasks" />
    </Switch>
  );
}

const App = () => {
  const { instance, accounts, inProgress } = useMsal();
  const apiTokenProvider = React.useMemo(
    () => () => acquireApiToken(instance, accounts),
    [instance, accounts]
  );

  // API views start their own effects during the first authenticated render.
  // Install the provider before those children render so a directly opened
  // route cannot race the parent's passive effect and fail before making a
  // request. Keep cleanup identity-aware so an older render cannot clear a
  // newer provider.
  sec.setAccessToken(apiTokenProvider);

  React.useEffect(() => {
    selectAccount(instance, accounts);
    return () => {
      if (sec.getAccessToken() === apiTokenProvider) {
        sec.setAccessToken(null);
      }
    };
  }, [instance, accounts, apiTokenProvider]);

  if (inProgress !== InteractionStatus.None) {
    return (
      <Provider store={appStore}>
        <LoadingOverlay loading={true} />
      </Provider>
    );
  }
  return (
    <Provider store={appStore}>
      <BrowserRouter>
        <ConnectedRouter history={history}>
          <div className="app-wrapper">
            <Suspense fallback={null}>
              <Helmet
                titleTemplate="%s | Korhonen TMS"
                defaultTitle="Korhonen TMS"
              >
                <meta name="description" content="Transport Mikko Korhosen FleetAdmin TMS -seurantanäkymä" />
              </Helmet>
              <Navbar location={history.location} />
              <AppRoutes />
              <ToastContainer />
              <ModalMain />
              <LoadingOverlay />
              <CookiesProvider />
            </Suspense>
          </div>
        </ConnectedRouter>
      </BrowserRouter>
    </Provider>
  );
};

export default App;
